import { useEffect, useMemo, useRef, useState } from 'react';
import { classifyGenre } from '../../music/classifier';
import { deriveMood, type DriveState } from '../../music/mood-derivation';
import type { MusicGenre, MusicMood, MusicVibeSharing } from '../../music/types';
import { useNowPlaying } from './useNowPlaying';
import { useMusicDictionary } from './useMusicDictionary';
import type { NowPlayingTrack } from '../../native/music-listener-native';
import type { MusicDictionary } from '../../music/types';
import type { EchoShareBody } from '../../cloud/api';
import { fetchNearbySameGenre, postMusicSample, type MusicSampleBody } from '../../cloud/music-api';

export interface UseMusicVibeState {
    enabled: boolean;
    genre: MusicGenre | null;
    mood: MusicMood | null;
    nearbySameGenrePing: boolean;
    attachToEcho: (body: EchoShareBody) => EchoShareBody;
}

interface UseMusicVibeArgs {
    musicVibeSharing: MusicVibeSharing;
    driveState: DriveState;
    h3Cell?: string | null;
}

const QUEUE_MAX = 32;
const QUEUE_MAX_AGE_MS = 24 * 60 * 60 * 1000;
const UPLOAD_INTERVAL_MS = 3 * 60 * 1000;
const PING_INTERVAL_MS = 5 * 60 * 1000;
const PING_VISIBLE_MS = 30 * 1000;

const sampleQueue: Array<{ body: MusicSampleBody; queuedAt: number }> = [];

export function useMusicVibe({ musicVibeSharing, driveState, h3Cell }: UseMusicVibeArgs): UseMusicVibeState {
    const { current } = useNowPlaying();
    const { dictionary } = useMusicDictionary();
    const [nearbySameGenrePing, setNearbySameGenrePing] = useState(false);
    const lastTrackRef = useRef<string | null>(null);
    const lastUploadRef = useRef(0);
    const lastPingRef = useRef(0);

    const state = useMemo(() => {
        const state = computeMusicVibe({ current, dictionary, musicVibeSharing, driveState });
        return {
            ...state,
            nearbySameGenrePing,
            attachToEcho: (body: EchoShareBody) => attachMusicVibeToEcho(body, state),
        };
    }, [current, dictionary, driveState, musicVibeSharing, nearbySameGenrePing]);

    useEffect(() => {
        if (
            musicVibeSharing !== 'places_and_routes'
            || driveState === 'IDLE'
            || !h3Cell
            || state.genre === null
            || current?.state !== 'playing'
        ) {
            return;
        }

        const body: MusicSampleBody = {
            h3: h3Cell,
            genre: state.genre,
            ...(state.mood ? { mood: state.mood } : {}),
            drive_state: driveState,
        };

        let disposed = false;
        const upload = (reason: 'track' | 'timer') => {
            const now = Date.now();
            const trackKey = current ? `${current.packageName}:${current.title}:${current.artist}` : '';
            if (reason === 'track') {
                if (lastTrackRef.current === trackKey) return;
                lastTrackRef.current = trackKey;
            } else if (now - lastUploadRef.current < UPLOAD_INTERVAL_MS) {
                return;
            }
            lastUploadRef.current = now;
            void flushQueue().finally(async () => {
                try {
                    await postMusicSample(body);
                } catch {
                    enqueueSample(body);
                    return;
                }

                if (now - lastPingRef.current < PING_INTERVAL_MS) return;
                lastPingRef.current = now;
                try {
                    const ping = await fetchNearbySameGenre({ h3: h3Cell, genre: state.genre! });
                    if (!disposed && ping.present) {
                        setNearbySameGenrePing(true);
                        window.setTimeout(() => {
                            if (!disposed) setNearbySameGenrePing(false);
                        }, PING_VISIBLE_MS);
                    }
                } catch {
                    // Nearby pings are optional UI sugar; a failed ping must not
                    // duplicate an already accepted music sample.
                }
            });
        };

        upload('track');
        const interval = window.setInterval(() => upload('timer'), UPLOAD_INTERVAL_MS);
        return () => {
            disposed = true;
            window.clearInterval(interval);
        };
    }, [current, driveState, h3Cell, musicVibeSharing, state.genre, state.mood]);

    return state;
}

export function computeMusicVibe({
    current,
    dictionary,
    musicVibeSharing,
    driveState,
}: {
    current: NowPlayingTrack | null;
    dictionary: MusicDictionary | null;
    musicVibeSharing: MusicVibeSharing;
    driveState: DriveState;
}): Omit<UseMusicVibeState, 'attachToEcho' | 'nearbySameGenrePing'> {
    if (musicVibeSharing === 'off') return { enabled: false, genre: null, mood: null };
    if (!dictionary) return { enabled: true, genre: null, mood: null };
    if (current?.state !== 'playing') return { enabled: true, genre: null, mood: null };

    const classification = classifyGenre(current, dictionary);
    if (classification === null || classification === 'other') {
        return { enabled: true, genre: null, mood: null };
    }

    return {
        enabled: true,
        genre: classification,
        mood: deriveMood(classification, driveState),
    };
}

export function attachMusicVibeToEcho(
    body: EchoShareBody,
    state: Pick<UseMusicVibeState, 'enabled' | 'genre' | 'mood'>,
): EchoShareBody {
    if (!state.enabled || state.genre === null) return body;
    return {
        ...body,
        music_genre: state.genre,
        ...(state.mood ? { music_mood: state.mood } : {}),
    };
}

function enqueueSample(body: MusicSampleBody): void {
    const now = Date.now();
    sampleQueue.push({ body, queuedAt: now });
    while (sampleQueue.length > QUEUE_MAX) sampleQueue.shift();
}

async function flushQueue(): Promise<void> {
    const now = Date.now();
    while (sampleQueue.length > 0) {
        const item = sampleQueue[0]!;
        if (now - item.queuedAt > QUEUE_MAX_AGE_MS) {
            sampleQueue.shift();
            continue;
        }
        await postMusicSample(item.body);
        sampleQueue.shift();
    }
}
